<template>
  <div v-bind:class="{'plan-today': $moment().isSame($moment(date), 'day') }">
    <div class="row row__filters" style="margin-bottom: 20px;">
      <div class="col-lg-2 col-md-3">
        <app-datepicker v-model="date" :clear-button="false"></app-datepicker>
      </div>
      <div class="col-lg-2 col-md-3">
        <app-avatar-list :users="channelUsers"></app-avatar-list>
      </div>

      <div class="col-lg-8 col-md-6" style="display: flex; flex-direction: row; justify-content: flex-end; align-items: center;">
        <div style="padding-right: 10px;" v-show="saving" class="text-muted">
          <i class="fa fa-spin fa-spinner"></i> Saving...
        </div>

        <button class="btn btn-md btn-primary" @click="confirmFinalize" :disabled="enableFinalizeAllButton" style="margin-right: 10px; position: relative; padding-left: 63px;">
          <svg style="position: absolute; left: 20px; margin: 0;" height="24px" width="24px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#000000">
            <path d="M0 0h24v24H0V0z" fill="none"/>
            <path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"/>
          </svg>
          Planning definitief maken
        </button>

        <button class="btn btn-md btn-default" @click="reload" :disabled="!loaded" v-if="!isHistory" tabindex="0">
          <svg class="pull-left" xmlns="http://www.w3.org/2000/svg" height="20" width="24" style="margin-left: 0; margin-right: 16px;">
            <path d="M12.05 20.15q-3.4 0-5.8-2.363-2.4-2.362-2.4-5.712v-.2L2.35 13.4.9 11.925l4.1-4.1 4.1 4.1L7.65 13.4l-1.5-1.525v.175q0 2.425 1.713 4.125 1.712 1.7 4.187 1.7.65 0 1.288-.163.637-.162 1.262-.487l1.675 1.675q-.975.625-2.05.937-1.075.313-2.175.313ZM19 16.2l-4.1-4.125 1.45-1.45 1.525 1.5v-.15q0-2.425-1.725-4.125-1.725-1.7-4.2-1.7-.65 0-1.287.15-.638.15-1.263.475L7.725 5.1q1-.625 2.063-.925 1.062-.3 2.162-.3 3.4 0 5.8 2.35t2.4 5.725v.175l1.5-1.5 1.475 1.45Z"/>
          </svg>
          Herladen
        </button>
      </div>
    </div>

    <div class="alert alert-warning m-top-15" v-if="isHistory">
      Je bekijkt een oude planning waardoor je geen wijzingen kunt aanbrengen.
    </div>

    <div class="loaderui" v-bind:class="{ 'loaderui-loaded': loaded }">
      <driver-availability-multiselect
        ref="multiselect"
        v-if="activeGroupForDriver"
        :value="(activeGroupForDriver && activeGroupForDriver.driver) ? activeGroupForDriver.driver : undefined"
        :options="Object.values(allDrivers)"
        :show-availabilities="false"
        :show-availability-icon="true"
        :show-avatar="true"
        :prevent-autofocus="true"
        :tabindex="-1"
        :option-height="50"
        :multiple="false"
        :close-on-select="true"
        :clear-on-select="false"
        :preserve-search="false"
        placeholder="Kies chauffeur..."
        label="name"
        track-by="id"
        :preselect-first="false"
        :taggable="false"
        style="width: 250px; flex-shrink: 0; background-color: #fff; transform: translateY(-2px); margin-top: 2px; position: absolute; z-index: 500; "
        @open="closeTooltips()"
        @close="closeTooltips()"
        @select="(selected) => driverSelected(selected, activeGroupForDriver)"
      ></driver-availability-multiselect>

      <div class="main-groups">
        <template v-for="mainGroup in mainJobGroups">
          <div class="col-group" :class="{ 'col-group-closed': mainGroup.closed}">
            <div class="card card-group-header card-dark" :class="{'m-bottom-10': mainGroup.id !== 4 }" style="position: relative; border-top-left-radius: 8px; border-top-right-radius: 8px" :style="[mainGroup.id !== 4 ? { 'border-radius': '8px' } : '']" @click="toggleMainGroup(mainGroup)">
              <span class="group-title" style="display: inline-block; min-width: 100px; color: white; padding-right: 20px;">{{ mainGroup.name }}</span>

              <small style="color: white; font-size: 12px;">{{ mainGroup.jobCount }} {{ mainGroup.jobCount === 1 ? 'rit' : 'ritten' }} <template v-if="mainGroup.id !== 4">in {{ mainGroup.groupCount }} {{ mainGroup.groupCount === 1 ? 'groep' : 'groepen' }}</template></small>

              <div class="toggle cursor-pointer" style="right: 0;">
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#ffffff">
                  <path d="M0 0h24v24H0V0z" fill="none"/>
                  <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14l-6-6z"/>
                </svg>
              </div>
            </div>

            <div class="groups" v-if="!mainGroup.closed">
              <div class="row job-group" id="group-sortable">
                <template v-for="group in groups">
                  <div v-if="group && group.jobs && group.jobs.length > 0 && group.group.main_group_id === mainGroup.id" :data-groupid="group.group.uuid" :key="`group-${group.group.uuid}`" :class="{ 'col-group-closed': group.group.closed, 'col-group-unplanned': group.group.uuid === 'plan' }" class="col-xs-12 col-group">
                    <div v-if="group.group.uuid !==  null && group.group.uuid !== 'plan'" :class="{'card-dark': group.group.uuid === 'plan'}" class="card card-group-header" @click="toggleGroup(group, $event)" @contextmenu.prevent="openContextMenu({'evt': $event, 'type': 'group', 'data': group})">
                      <div class="row d-flex-a-center">
                        <div class="col d-flex-a-center" style="width: 100%">
                          <label class="custom-checkbox checkbox-secondary p-left-15" v-if="group.group.uuid !== 'plan' && !isHistory" @click.stop>
                            <input type="checkbox" class="main-checkbox" :value="group.group.uuid" v-model="selectedGroups">
                            <span class="label-text"></span>
                          </label>

                          <div v-if="group.group.finalized_at && group.group.finalized_at !== null" class="finalized" data-toggle="tooltip" data-container="body" :title="$moment(group.group.finalized_at).format('HH:mm') + ' door ' + group.group.finalized_by">
                            <span class="label label-primary" style="width: 70px; height: 19px; font-weight: 400; display: block; font-size: 11px; padding: 0; line-height: 19px; margin: 0 15px">Definitief</span>
                          </div>
                          <div v-else>
                            <span class="definitief-placeholder" style="width: 100px; display: block;"></span>
                          </div>

                          <div class="starttime" style="padding-top: 2px;" v-if="group.driver">
                            <span class="driver__availability_time">
                              <span>
                                <svg xmlns="http://www.w3.org/2000/svg" height="17px" style="margin-bottom: -3px;" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>

                                {{ startTimeGroup(group) }}
                              </span>
                            </span>
                          </div>

                          <div v-else class="starttime"></div>

                          <div
                            v-if="group.driver"
                            :id="`driver-group-${group.group.uuid}`"
                            @click.left.prevent.stop="setDriverSelect(group)"
                            @click.right.prevent.stop
                            style="display:flex; width: 250px; padding:10px 5px; height: 52px; align-items: center;"
                            data-toggle="tooltip"
                            data-container="body"
                            :title="group.driver ? driverAvailability(group.driver) : null"
                          >
                            <img :src="group.driver.avatar_url" class="img-circle hidden-xs" style="height: 25px;">
                            <img :src="group.driver.availability_icon">

                            {{ group.driver.name }}
                          </div>

                          <div
                            class="driver-select-placeholder"
                            :id="`driver-group-${group.group.uuid}`"
                            v-else
                            style="height: 52px; width: 250px; display: flex; align-items: center; padding:10px 5px;"
                            @click.left.prevent.stop="setDriverSelect(group)"
                          >Selecteer chauffeur...</div>

                          <div v-if="group.group.uuid !== 'plan'" class="status-group"></div>

                          <span class="group-route" v-html="groupRoute(group)" @click.ajaxprevent></span>

                          <span class="group-name" :contenteditable="group.group.uuid !== 'plan' && !isHistory" :data-group="group.group.uuid" @keydown.enter.prevent="$event.target.blur()" @blur="onChangeGroupName" @input="$event => echoChannel.whisper('group:name_changed', { uuid: group.group.uuid, name: $event.target.innerText })" @click.stop style="">
                            {{ group.group.name }}
                          </span>

                          <div class="toggle cursor-pointer">
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14l-6-6z"/></svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                        class="card card-fancy-table card-group-table"
                        style="min-height: unset;"
                    >
                      <div class="table-responsive" style="min-height: unset;">
                        <table class="table table-striped table-bordered table-sortable-jobs">
                          <thead>
                          <tr>
                            <th v-if="!isHistory" style="width: 40px; text-align: center"></th>
                            <th style="width: 80px; text-align: center">#</th>
                            <th>Route</th>
                            <th style="width: 250px;">Klant</th>
                            <th style="width: 200px;">Tijdstip</th>
                            <th style="width: 200px;">Status</th>
                            <th style="width: 250px;">Kenteken</th>
                            <th style="width: 180px;">Services</th>
                            <th style="width: 60px;"><svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg></th>
                          </tr>
                          </thead>
                          <tbody
                            class="plan-body"
                            :data-groupid="group.group.uuid"
                          >
                          <tr
                            v-for="job in group.jobs"
                            :id="`job-tr-${job.id}`"
                            :key="`job-tr-${job.id}`"
                            :data-jobid="job.id"
                            :class="{ 'finished': job.finished }"
                            @contextmenu.prevent="openContextMenu({'evt': $event, 'type': 'job', 'data': job})"
                          >
                            <td v-if="!isHistory">
                              <label class="custom-checkbox checkbox-secondary">
                                <input type="checkbox" class="main-checkbox" :value="job.id" v-model="selectedJobs">
                                <span class="label-text"></span>
                              </label>
                            </td>
                            <td style="text-align: center">
                              <a :href="`/ritten/bekijk/${job.id}`" target="_blank">{{ job.id }}</a>
                            </td>
                            <td style="white-space: normal; user-select: none" v-html="job.route_text"></td>
                            <td>
                              {{ job.company.name }}
                            </td>
                            <td>
                              <span v-if="job.time_type_text_abbreviation === 'GT'">
                                {{ job.time_type_text }}
                              </span>

                              <span v-else>
                              {{ job.time_type_text_abbreviation }} - {{ job.time_from_text }} - {{ job.time_to_text }}
                            </span>
                            </td>
                            <td class="status">
                              <div v-if="group.driver">
                                <div v-html="job.status_text_with_icon"></div>
                              </div>
                            </td>
                            <td v-html="job.licenses_text_with_icons"></td>
                            <td v-html="job.services_icon"></td>
                            <td>
                              <job-comment-counter
                                :ref="`jobCommentCounter-${job.id}`"
                                :job-id="job.id"
                                :job-deleted="job.deleted_at !== null"
                                :user="user"
                                :change-comment="false"
                                @open="jobCommentOpened(job.id)"
                                @close="openJobComments = []"
                                @change="counterUpdate($event, job)"
                              >
                                {{ job.job_comments_count }}
                              </job-comment-counter>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </div>

      <app-action-bar
        ref="actionBar"
        :show="showActionBar"
        :actions="actions"
        :groups="activeGroups"
        :selection-text="actionBarSelectionText"
        @close="selectedJobs.splice(0); selectedGroups.splice(0); contextMenuSelection = null;"
        @action="actionBarAction"
      >
      </app-action-bar>

      <app-context-menu
        ref="menu"
        :actions="actions"
        :groups="activeGroups"
        :selection-text="contextMenuSelectionText"
        @action="contextMenuAction"
        @close="contextMenuClosed" />

      <driver-modal
        v-if="loaded"
        ref="driverModal"
        :date="currentDate"
        :group="activeGroup"
        :availabilities="availabilities"
        :selected-groups-count="selectedGroups.length"
        @close="showDriverModal = false; selectedGroups.splice(0); contextMenuSelection = null;"
        @selected="attachDriver" />
    </div>
  </div>
</template>

<script>
import TooltipMixin from './mixins/TooltipMixin.js';
import JobActionsMixin from './mixins/JobActionsMixin.js';
import GroupActionsMixin from './mixins/GroupActionsMixin.js';
import AppActionBar from './../AppActionBar.vue';
import AppContextMenu from './../AppContextMenu.vue';
import AppAvatarList from '../AppAvatarList.vue';
import JobGroup from './JobGroup.vue';
import DriverModal from './DriverModal.vue';
import JobCommentCounter from '../Job/Comments/Counter.vue';
import DriverAvailabilityMultiSelect from "../drivers/DriverAvailabilityMultiSelect.vue";
import {v4 as uuidv4} from 'uuid';
import AppMultiselect from "../AppMultiselect.vue";

export default {
  name: "Planscreen",

  mixins: [
    TooltipMixin,
    JobActionsMixin,
    GroupActionsMixin
  ],

  components: {
    AppMultiselect,
    JobCommentCounter,
    DriverModal,
    JobGroup,
    AppAvatarList,
    AppActionBar,
    AppContextMenu,
    DriverAvailabilityMultiSelect,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },

    availabilities: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      loaded: false,
      saving: false,
      date: null,
      search: null,
      mainGroups: [
        {id: 0, name: 'Checken', closed: false},
        {id: 1, name: 'Normaal', closed: false},
        {id: 2, name: 'Gaat goed', closed: false},
        {id: 3, name: 'Afgerond', closed: false},
        {id: 5, name: 'Doorplannen', closed: false},
        {id: 4, name: 'In te plannen', closed: false},
      ],
      groups: [],
      dragging: false,
      channelUsers: [],
      contextMenuSelection: null,
      showActionBar: false,
      showContextMenu: false,
      showDriverModal: false,
      selectedJobs: [],
      selectedGroups: [],
      sortables: [],
      openJobComments: [],
      echoChannel: null,
      previousRequest: null,
      allDrivers: [],
      selectedDrivers: [],
      activeGroupForDriver: null,
    }
  },

  created() {
    document.addEventListener( 'visibilitychange' , () => {
      this.echoChannel.whisper(document.hidden ? 'offline' : 'online', this.user);
    }, false );

    window.onblur = () => {
      this.echoChannel.whisper('offline', this.user);
    };

    window.addEventListener('beforeunload', this.beforeWindowUnload);

    window.onfocus = () => {
      this.echoChannel.whisper('online', this.user);
    };

    Echo.private('availabilities')
      .listen('.user.availability.updated', (e) => {
        if (!moment(this.date).startOf('day').isSame(moment(e.date).startOf('day'))) {
          return;
        }

        if (this.allDrivers.hasOwnProperty(e.user_id)) {
          this.allDrivers[e.user_id].availability_icon = e.icon;

          this.reloadTooltips();
        }

        const group = this.groups.find(group => {
          const driverId = group.driver && group.driver.id ? group.driver.id : null

          return e.user_id === driverId;
        });

        if (group) {
          group.driver.availabilities = e.availabilities;
          group.driver.availability_icon = e.icon;

          setTimeout(() => {
            this.reloadTooltips();
          }, 100);
        }

      })
      .listen('.user.availability.confirmed', (e) => {
        const group = this.groups.find(group => {
          const driverId = group.driver && group.driver.id ? group.driver.id : null

          return e.user_id === driverId;
        });

        if (group) {
          group.driver.activeAvailability.title = e.title;
          group.driver.activeAvailability.icon = e.icon;

          this.reloadTooltips();
        }

        if (this.allDrivers.hasOwnProperty(e.user_id)) {
          this.allDrivers[e.user_id].availability_icon = e.icon;
        }
      })

    if (this.date === null) {
      const params = new URLSearchParams(location.search);
      const dateFromUrl = params.get('date');
      if (dateFromUrl) {
        this.date = dateFromUrl;
      } else {
        this.date = new Date().toISOString().slice(0,10);
      }
    }
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
  },

  watch: {
    selectedGroups() {
      this.activeGroupForDriver = null;
      const selectedGroups = this.selectedGroups.filter(Boolean).length;
      const selectedJobs = this.selectedJobs.filter(Boolean).length;

      if (selectedGroups > 0 && selectedJobs < 1) {
        this.showActionBar = true;
      } else {
        this.showActionBar = selectedJobs > 0 && selectedGroups < 1;
      }

      this.reloadTooltips(100);
    },

    selectedJobs() {
      this.activeGroupForDriver = null;
      const selectedGroups = this.selectedGroups.filter(Boolean).length;
      const selectedJobs = this.selectedJobs.filter(Boolean).length;

      if (selectedJobs > 0 && selectedGroups < 1) {
        this.showActionBar = true;
      } else {
        this.showActionBar = selectedGroups > 0 && selectedJobs < 1;
      }

      this.selectedJobs.forEach((jobId) => {
        const tr = document.getElementById(`job-tr-${jobId}`);
        Sortable.utils.select(tr);
      });
    },

    date: {
      immediate: false,
      handler(newValue, oldValue){
        if (this.date === null) {
          const params = new URLSearchParams(location.search);
          const dateFromUrl = params.get('date');
          if (dateFromUrl) {
            this.date = dateFromUrl;
          } else {
            this.date = new Date().toISOString().slice(0,10);
          }
        }

        this.loaded = false;
        this.activeGroupForDriver = null;
        this.resetSelection();

        const oldDate = moment(oldValue).format('YYYY-MM-DD');
        const date = moment(this.date).format('YYYY-MM-DD');

        Echo.leave(`plan.${oldDate}`);

        this.fetchMainGroups();

        this.fetchDrivers(() => {
          this.fetch();
        });

        if (history.pushState) {
          const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?date=' + date;
          window.history.pushState({ path: newUrl }, '' , newUrl);
        }

        this.echoChannel = Echo.join(`plan.${date}`)
          .here(users => {
            this.channelUsers = users;
          })
          .joining(user => {
            this.channelUsers = _.uniqWith([...this.channelUsers, user], _.isEqual);

            this.reloadTooltips();
          })
          .leaving((user) => {
            this.channelUsers = this.channelUsers.filter(channelUser => {
              return channelUser.id !== user.id;
            })
          })
          .listen('.job.driver_planned', event => {
            this.attachDriver(event, [event.groupUuid])

            const group = this.groups.find(group => {
              return group.group.uuid == event.groupUuid;
            });

            if (group) {
              if (event.driver_id) {
                group.driver = (event.driver_id in this.allDrivers) ? this.allDrivers[event.driver_id] : null;
              } else if (group.driver) {
                group.driver = null;
              }

              if (event.startTime) {
                group.startTime = event.startTime;
              }

              group.jobs.forEach((job, index) => {
                this.changeJob(job.id, (job) => {
                  job.status_text_with_icon = event.status_text_with_icon[index];
                  job.startTime = event.startTime;

                  return job;
                });
              });

              setTimeout(() => {
                this.updateGroupStatus(group.group.uuid);
              }, 500);
            }
          })
          .listen('.job.license_changed', event => {
            this.changeJob(event.jobId, (job) => {
              job.licenses_text_with_icons = event.licenses_text_with_icons;

              return job;
            });
          })
          .listen('.job.driver_delayed', event => {
            this.changeJob(event.jobId, (job) => {
              job.current_jobcheck.delay.time = event.delay.arrived_at;
              job.current_jobcheck.delay.delay = event.delay.is_delay;
              job.current_jobcheck.delay.explanation = event.delay.explanation;

              job.status_text_with_icon = event.status_text_with_icon;

              return job;
            });

            this.updateGroupStatus(event.groupUuid, event.status_text_with_icon);

            this.reloadTooltips();
          })
          .listen('.job.status_updated', event => {
            this.changeJob(event.jobId, (job) => {
              job.status_text_with_icon = event.status_text_with_icon;

              if (event.job_finished) {
                job.finished = event.job_finished;
              }

              setTimeout(() => {
                this.updateGroupStatus(event.groupUuid);
              }, 500);

              return job;
            });

            if (event.current_route_index) {
              const group = this.groups.find(group => {
                return group.group.uuid == event.groupUuid;
              });

              if (group && group.group) {
                group.group.current_index = event.current_route_index;
              }
            }

            this.reloadTooltips();
          })
          .listen('.job.approved', event => {
            const jobs = event[0].jobs;
            if (jobs.length) {
              const newJob = jobs[0];

              const changed = this.changeJob(newJob.id, job => {
                return newJob;
              });

              if (!changed && !newJob.group_id) {
                const planGroup = this.groups.find(group => {
                  return group.group.uuid === 'plan';
                });

                if (planGroup && planGroup.jobs) {
                  planGroup.jobs.push(newJob);
                }
              }
            }
          })
          .listen('.job.comment_created', event => {
            this.changeJob(event.jobId, (job) => {
              job.job_comments_count = event.commentCount;

              return job;
            });

            if (! event.description.includes('formulier') && event.role !== 'Administrator') {
              let name = event.company.name;
              if (event.role === 'Chauffeur') {
                name = event.user.name;
              }
            }
          })
          .listen('.job.comment_deleted', event => {
            this.changeJob(event.jobId, (job) => {
              job.job_comments_count = event.commentCount;

              return job;
            });
          })
          .listenForWhisper('online', event => {
            const user = this.channelUsers.find(user => event.id === user.id);
            user.online = true;
          })
          .listenForWhisper('offline', event => {
            const user = this.channelUsers.find(user => event.id === user.id);
            user.online = false;
          })
          .listenForWhisper('job:create', event => {
            this.catchWhisperJob('job:create', event)

          })
          .listen('.job.create', event => {
            this.backupJobWhisperAction('job:create', event);
          })
          .listenForWhisper('job:move', event => {
            this.catchWhisperJob('job:move', event);
          })
          .listen('.job.move', event => {
            this.backupJobWhisperAction('job:move', event);
          })
          .listenForWhisper('job:move_down', event => {
            this.catchWhisperJob('job:move_down', event);
          })
          .listen('.job.move_down', event => {
            this.backupJobWhisperAction('job:move_down', event);
          })
          .listenForWhisper('job:move_up', event => {
            this.catchWhisperJob('job:move_up', event);
          })
          .listen('.job.move_up', event => {
            this.backupJobWhisperAction('job:move_up', event);
          })
          .listenForWhisper('job:remove', event => {
            this.catchWhisperJob('job:remove', event);
          })
          .listen('.job.remove', event => {
            this.backupJobWhisperAction('job:remove', event);
          })
          .listenForWhisper('job:reorder', event => {
            const newGroups = _.cloneDeep(this.groups);

            const groupFrom = newGroups.find(group => {
              return group.group.uuid === event.groupIdFrom;
            });
            const groupTo = newGroups.find(group => {
              return group.group.uuid === event.groupIdTo;
            });

            const jobs = [];
            if (groupFrom && groupFrom.jobs) {
              groupFrom.jobs = groupFrom.jobs.filter(job => {
                if (event.resultTo.includes(job.id)) {
                  jobs.push(job);

                  return false;
                }

                return true;
              });
            }

            if (groupTo && groupTo.jobs) {
              groupTo.jobs = [...groupTo.jobs, ...jobs].map(job => {
                job.order = event.resultTo.indexOf(job.id);

                if (job.order === 0 && event.time) {
                  job.status_text_with_icon = `<div><img src="/images/job_statuses/drop.svg" class=""><span>Starttijd: ${event.time}</span></div>`;
                } else if (job.status_text_with_icon && job.status_text_with_icon.indexOf('Starttijd') > -1) {
                  job.status_text_with_icon = '';
                }

                return job;
              }).sort((a, b) => {
                return a.order - b.order;
              });
            }

            this.groups = newGroups;
          })
          .listenForWhisper('group:name_changed', event => {
            const newGroups = _.cloneDeep(this.groups);

            const group = newGroups.find(group => {
              return group.group.uuid === event.uuid;
            });

            group.group.name = event.name;

            this.groups = newGroups;
          })
          .listenForWhisper('group:deattach_driver', event => {
            this.groupAction('deattach_driver', null, event.selection);
          })
          .listenForWhisper('group:attach_driver', event => {
            this.attachDriver(event.event, event.selection, false, event.event.removeFinalize);
          })
          .listenForWhisper('group:change_startdate', event => {
            this.groupAction('change_startdate', { subAction: event.startTime }, event.selection);
          })
          .listenForWhisper('group:move_down', event => {
            this.groupAction('move_down', null, event.selection);
          })
          .listen('.group.move', event => {
            const groupEventAlreadyFired = this.groupEventHistory.includes(event.actionId);

            if (!groupEventAlreadyFired) {

            }
          })
          .listenForWhisper('group:move_up', event => {
            this.groupAction('move_up', null, event.selection);
          })
          .listenForWhisper('group:move_to_bottom', event => {
            this.groupAction('move_to_bottom', null, event.selection);
          })
          .listenForWhisper('group:move_to_top', event => {
            this.groupAction('move_to_top', null, event.selection);
          })
          .listenForWhisper('group:remove', event => {
            this.groupAction('remove', null, event.selection);
          })
          .listenForWhisper('group:finalize', event => {
            this.groupAction('finalize', null, event.selection);
          })
          .listen('.group.finalized', event => {
            this.groupAction('finalize', null, event.selection);
          })
          .listenForWhisper('group:assign_to_maingroup', event => {
            this.groupAction('assign_to_maingroup', {subAction: {id: event.main_group_id}}, event.selection);
          })
          .error(error => {
            console.error(error);
          })
      }
    },
  },

  computed: {
    actions() {
      if ((this.contextMenuSelection && this.contextMenuSelection.type === 'group') || this.selectedGroups.length) {
        return this.groupActions;
      }

      return this.jobActions;
    },

    groupList() {
      if (this.groups.length) {
        return this.groups.map(group => {
          group.group.jobCount = group.jobs.length;

          return group.group;
        });
      }

      return [];
    },

    groupListFiltered() {
      if (this.groupList.length) {
        return _.orderBy(this.groupList
          .filter(group => {
            return group.uuid !== 'plan' && group.jobCount > 0;
          }), ['main_group_id', 'order'], ['asc', 'desc']);
      }
    },

    mainJobGroups() {
      return this.mainGroups.map(mainGroup => {
        const groups = this.groups.filter(group => group.group.main_group_id === mainGroup.id);

        const jobCount = _.sumBy(groups.map(group => {
          return group.jobs.length;
        }));

        return { ...mainGroup, jobCount: jobCount, groupCount: groups.length };
      });
    },

    actionBarSelectionText() {
      const groupCount = this.selectedGroups.length;
      const jobCount = this.selectedJobs.length;

      if (groupCount > 1) {
        return `${groupCount} groepen geselecteerd`;
      } else if (groupCount > 0) {
        return `${groupCount} groep geselecteerd`;
      } else if (jobCount > 1) {
        return `${jobCount} ritten geselecteerd`;
      }

      return `${jobCount} rit geselecteerd`;
    },

    contextMenuSelectionText() {
      if (this.contextMenuSelection && this.contextMenuSelection.type === 'job') {
        return 'Rit #' + this.contextMenuSelection.data.id;
      }

      if (this.contextMenuSelection && this.contextMenuSelection.type === 'group') {
        return this.contextMenuSelection.data.group.name;
      }
    },

    activeGroups() {
        return this.groups.filter(group => {
          return this.getSelection().includes(group.group.uuid);
        });
    },

    activeGroup() {
        return this.activeGroups[0];
    },

    currentDate() {
      return moment(this.date);
    },

    isHistory() {
      return this.currentDate.isBefore(moment(), 'day');
    },

    hasJobs() {
      return this.groups.some(group => group && group.jobs && group.jobs.length > 0);
    },

    hasGroups() {
      return this.groups.length > 1;
    },

    groupsWithDriver() {
      return this.groups.filter(group => {
        return group.driver !== null && group.group.finalized_at === null
      });
    },

    groupsFinalized() {
      return this.groups.filter(group => group.group.finalized_at && group.group.finalized_at !== null && group.group.uuid !== 'plan');
    },

    enableFinalizeAllButton() {
      return !(this.groupListFiltered && this.groupListFiltered.length > 0 && this.groupsWithDriver.length > 0);
    },
  },

  methods: {
    setDriverSelect(group) {
      this.activeGroupForDriver = group;

      this.$nextTick(() => {
        const multiselect = this.$refs.multiselect;
        const groupDriver = document.getElementById(`driver-group-${group.group.uuid}`);

        const colGroup = groupDriver.closest('.col-group');
        const groupOffsetTop = colGroup.offsetTop;

        const driverRect = groupDriver.getBoundingClientRect();
        multiselect.$el.style.top = `${groupOffsetTop}px`;
        multiselect.$el.style.left = `${driverRect.left - 25}px`;

        multiselect.activate();
      })

    },
    openContextMenu(evt) {
      this.resetSelection();

      if (evt.type === 'group' && evt.data.group.uuid === 'plan') {
        return;
      }

      if (! this.isHistory) {
        this.contextMenuSelection = {
          type: evt.type,
          data: evt.data,
        };

        this.$refs.menu.open(evt.evt);
        this.contextMenuOpen = true;
      }
    },

    actionBarAction(action) {
      if (action.event.callback(action)) {
        this.selectedJobs.splice(0);
        this.selectedGroups.splice(0);

        return true;
      }

      return false;
    },

    contextMenuAction(action) {
      this.actionBarAction(action);

      this.$refs.menu.close();
    },

    contextMenuClosed(evt) {
      this.contextMenuOpen = false;

      if (!evt) {
        this.contextMenuSelection = null;
      }

      if (evt && evt.action && evt.action !== 'attach_driver') {
        setTimeout(() => {
          this.contextMenuSelection = null;
        }, 200);
      }
    },

    fetch(callback) {
      this.$http.get('/plannen/ajax', {
        before(request) {
          if (this.previousRequest) {
            this.previousRequest.abort();
          }
          this.previousRequest = request;
        },
        params: {
          date: moment(this.date).format('YYYY-MM-DD')
        },
      }).then((response) => {
        this.groups = response.data.sort((a, b) => a.order - b.order).map(group => {
          group.driver = (group.driver_id && group.driver_id in this.allDrivers) ? this.allDrivers[group.driver_id] : null;

          return group;
        });
        setTimeout(() => {
          this.loaded = true;
          this.reloadTooltips();
          this.setSortable();

          this.updateGroupStatus();
        }, 1000);

        if (callback) {
          callback();
        }
      });
    },

    fetchMainGroups() {
      this.$http.get('/plannen/maingroups', {
        params: {
          date: moment(this.date).format('YYYY-MM-DD')
        },
      }).then((response) => {
        if (response && response.data) {
          response.data.forEach(group => {
            this.mainGroups.map(mainGroup => {
              if (mainGroup.id === group.main_group_id) {
                return mainGroup.closed = !!group.closed;
              }
            })
          });
        }
      });
    },

    fetchDrivers(callback) {
      const filters = {
        pagination: false,
        date: moment(this.date).format('YYYY-MM-DD')
      }

      this.$http.get('/chauffeurs/ajaxinline', {
        params: filters
      }).then((response) => {
        if (response && response.data) {
          this.allDrivers = response.data;
        }

        if (callback) {
          callback();
        }
      });
    },

    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array)
      }
    },

    onChangeGroupName(evt) {
      const newGroups = _.cloneDeep(this.groups);
      const actionId = uuidv4();

      const group = newGroups.find(group => {
        return group.group.uuid === evt.target.dataset.group;
      });

      group.group.name = evt.target.innerText;

      this.saving = true;
      this.$http.post('/plannen/rename-group?date=' + moment(this.date).format('YYYY-MM-DD') + '&actionId=' + actionId, {
        id: group.group.uuid,
        name: group.group.name,
      }).then(response => {
        this.saving = false;
      });

      this.groups = newGroups;
    },

    toggleGroup(group, event) {
      if (event.target.closest('.app-multiselect')) {
        return;
      }

      const newGroups = _.cloneDeep(this.groups);
      const actionId = uuidv4();

      const groupId = group.group.uuid;
      const newGroup = newGroups.find(group => {
        return group.group.uuid === groupId;
      })
      newGroup.group.closed = !group.group.closed;

      this.selectedJobs.splice(0);

      if (newGroup.group.uuid !== 'plan') {
        this.saving = true;
        this.$http.post('/plannen/toggle-group?date=' + moment(this.date).format('YYYY-MM-DD') + '&actionId=' + actionId, {
          id: newGroup.group.uuid,
          closed: newGroup.group.closed,
        }).then(response => {
          this.saving = false;
        });
      }

      this.groups = newGroups;
    },

    toggleMainGroup(mainGroup) {
      const newMainGroups = _.cloneDeep(this.mainGroups);
      const actionId = uuidv4();
      const mainGroupId = mainGroup.id;
      const newMainGroup = newMainGroups.find(mainGroup => {
        return mainGroup.id === mainGroupId;
      })
      newMainGroup.closed = !mainGroup.closed;

      this.saving = true;
      this.$http.post('/plannen/toggle-maingroup?date=' + moment(this.date).format('YYYY-MM-DD') + '&actionId=' + actionId, {
        mainGroupId: newMainGroup.id,
        closed: newMainGroup.closed,
      }).then(response => {
        this.saving = false;
      });

      this.mainGroups = newMainGroups;
    },

    driverSelected(selected, group) {
      this.attachDriver({
        driver_id: selected.id ? selected.id : null,
        time: (group.startTime && typeof group.startTime === 'string') ? group.startTime.substring(0, 5) : '08:00'
      }, [group.group.uuid], true, true)

      this.selectedGroups = [group.group.uuid];

      this.$refs.actionBar.fire(this.actions.find(action => action.action === 'change_startdate'));
    },

    attachDriver(event, initSelection = null, forceSave = false, removeFinalize = false) {
      const groupIds = initSelection ? initSelection : this.getSelection('group');
      const hasFinalGroup = groupIds.filter(groupId => {
        const group = this.groups.find(group => {
          return group.group.uuid == groupId;
        });

        return group.group.finalized_at !== null;
      }).length > 0;

      return this.confirmWhenPlanned(() => {
        let newGroups = _.cloneDeep(this.groups);
        const data = [];

        groupIds.forEach(groupId => {
          newGroups = newGroups.map(group => {
            if (group.group.uuid === groupId) {
              group.driver = event.driver_id ? this.allDrivers[event.driver_id] : null;
              group.driver_id = event.driver_id;

              if (removeFinalize) {
                group.group.finalized_at = null;
                group.group.finalized_by = null;
              }

              group.startTime = event.time;
              group.jobs[0].status_text_with_icon = `<div><img src="/images/job_statuses/drop.svg" class=""><span>Starttijd: ${event.time}</span></div>`;
            }

            return group;
          });

          data.push({
            id: groupId,
            driver: event.driver_id,
            time: event.time,
            date: this.date,
          });
        });

        this.groups = newGroups;
        this.reloadTooltips(1000);

        if (!initSelection || forceSave) {
          const actionId = uuidv4();
          this.saving = true;
          this.$http.post('/plannen/driver?date=' + moment(this.date).format('YYYY-MM-DD') + '&actionId=' + actionId, data).then(response => {
            this.saving = false;
          });

          data.forEach(group => {
            this.updateGroupStatus(group.id);
          });

          this.echoChannel.whisper('group:attach_driver', {
            selection: groupIds,
            event: {
              driver_id: event.driver_id,
              time: event.time,
              removeFinalize: forceSave,
            },
          });

          $(this.$refs['driverModal'].$el).modal('hide');
        }
      }, () => {
        this.selectedGroups = [];
      },  forceSave && hasFinalGroup);
    },

    counterUpdate(counter, job) {
      job.job_comments_count = counter;
    },

    changeJob(jobId, callback) {
      let changed = false;

      this.groups = this.groups.map(group => {
        group.jobs = group.jobs.map(job => {
          if (job && job.id === jobId) {
            changed = true;
            return callback(job);
          }

          return job;
        })

        return group;
      });

      return changed;
    },

    jobCommentOpened(openedJobId) {
      this.openJobComments.forEach(jobId => {
        this.$refs[`jobCommentCounter-${jobId}`][0].close();
      });

      this.openJobComments = [openedJobId];
    },

    setSortable() {
      this.sortables.forEach(sortable => {
        try {
          sortable.destroy();
        } catch (e) { }
      });

      if (this.isHistory) {
        return;
      }

      const sortableDiv = document.getElementsByClassName('plan-body');
      Array.from(sortableDiv).forEach(el => {
        const sortable = Sortable.create(el, {
          multiDrag: false,
          forceFallback: false,
          group: 'job',
          animation: 150,
          easing: "cubic-bezier(1, 0, 0, 1)",
          scroll: document.getElementById('main_content'),
          scrollSensitivity: 80,
          touchStartThreshold: 100,
          swapThreshold: 50,
          delay: 0,
          handle: 'tr',
          dataIdAttr: 'data-jobid',
          filter: '.ignore-elements',
          ghostClass: "sortable-ghost",
          selectedClass: "sortable-selected",
          chosenClass: "sortable-chosen",
          dragClass: "sortable-drag",
          onMove: () => { // disable drop when selected jobs from multiple groups
            const groups = [];

            this.selectedJobs.forEach(jobId => {
              const group = this.findGroupByJob(jobId, this.groups);
              if (! groups.includes(group.group.uuid)) {
                groups.push(group.group.uuid);
              }
            });

            return groups.length === 1;
          },
          onStart: (evt) => {
            document.body.classList.add('disable-nav-pointer');
            const jobId = parseInt(evt.item.getAttribute('data-jobid'));

            if (! this.selectedJobs.includes(jobId)) {
              this.selectedJobs.push(jobId);
            }
          },
          onEnd: (evt) => {
            document.body.classList.remove('disable-nav-pointer');
            const groupIdFrom = evt.from.getAttribute('data-groupid');
            const groupIdTo = evt.to.getAttribute('data-groupid');
            const selection = this.selectedJobs;

            const itemsTo = evt.to.children;
            const resultTo = [];
            const resultToOld = [];
            for (let i = 0; i < itemsTo.length; i++) {
              resultTo.push(parseInt(itemsTo[i].getAttribute('data-jobid')));
            }

            //undo the move in html
            const tagName = evt.item.tagName;
            const items = evt.from.getElementsByTagName(tagName);
            if (evt.oldIndex > evt.newIndex) {
              evt.from.insertBefore(evt.item, items[evt.oldIndex + 1]);
            } else {
              evt.from.insertBefore(evt.item, items[evt.oldIndex]);
            }

            for (let i = 0; i < itemsTo.length; i++) {
              resultToOld.push(parseInt(itemsTo[i].getAttribute('data-jobid')));
            }

            const newGroups = _.cloneDeep(this.groups);

            const groupFrom = newGroups.find(group => {
              return group.group.uuid === groupIdFrom;
            });
            const groupTo = newGroups.find(group => {
              return group.group.uuid === groupIdTo;
            });

            const jobs = [];
            if (groupFrom && groupFrom.jobs) {
              groupFrom.jobs = groupFrom.jobs.filter(job => {
                if (selection.includes(job.id)) {
                  jobs.push(job);

                  return false;
                }

                return true;
              });
            }

            if (groupTo && groupTo.jobs) {
              groupTo.jobs = [...groupTo.jobs, ...jobs].map((job, index) => {
                job.order = resultTo.indexOf(job.id);

                return job;
              }).sort((a, b) => {
                return a.order - b.order;
              });
            }

            if (groupFrom.group.id === groupTo.group.id && _.isEqual(resultTo, resultToOld)) {
              // drag drop ended without any change
            } else {
              this.confirmWhenPlanned(() => {
                this.groups = newGroups;

                this.echoChannel.whisper('job:reorder', {
                  groupIdFrom: groupIdFrom,
                  groupIdTo: groupIdTo,
                  resultTo: resultTo,
                  selected: selection,
                  startTime: groupTo.startTime,
                });

                let dataGroupFrom = [];
                if (groupFrom.group.id === 'plan') {
                  dataGroupFrom = [{
                    id: groupFrom.group.uuid,
                    jobs: groupFrom.jobs.filter(job => selection.includes(job.id)).map(job => job.id),
                    order: groupFrom.jobs.map(job => job.id),
                    startTime: groupFrom.startTime,
                    date: this.date,
                  }];
                }
                const data = [
                  ...dataGroupFrom,
                  {
                    id: groupTo.group.uuid,
                    jobs: groupTo.jobs.filter(job => selection.includes(job.id)).map(job => job.id),
                    order: groupTo.jobs.map(job => job.id),
                    startTime: groupTo.startTime,
                  }
                ];

                this.saving = true;
                const actionId = uuidv4();
                this.$http.post('/plannen/move-jobs?date=' + moment(this.date).format('YYYY-MM-DD') + '&actionId=' + actionId, data).then(response => {
                  this.saving = false;
                });

                this.setSortable();
              }, () => {},(groupFrom.group.uuid !== 'plan' && groupFrom.group.finalized_at !== null)
                  || (groupTo.group.uuid !== 'plan' && groupTo.group.finalized_at !== null)
              );
            }

            this.resetSelection();
          }
        });

        this.sortables.push(sortable);
      });
    },

    getSelection(type) {
      if (this.contextMenuSelection) {
        if (this.contextMenuSelection.type === 'group') {
          return [this.contextMenuSelection.data.group.uuid];
        }

        return [this.contextMenuSelection.data.id];
      }

      if (this.selectedGroups.length || type === 'group') {
          return this.selectedGroups;
      }

      return this.selectedJobs;
    },

    getSelectionType() {
      if (this.contextMenuSelection) {
        return this.contextMenuSelection.type;
      }

      if (this.selectedGroups.length) {
        return 'group';
      }

      return 'job';
    },

    scrollToGroup(groupId, highlight = false) {
      const groupElement = document.body.querySelector(`.col-group[data-groupid="${groupId}"]`);
      if (!groupElement) {
        console.warn('group not found', groupId);
        return;
      }

      groupElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      if (highlight) {
        groupElement.classList.add('highlighted');
        setTimeout(function () {
          groupElement.classList.remove('highlighted');
        }, 500);
      }
    },

    resetSelection() {
      document.body.querySelectorAll('.sortable-selected').forEach(node => {
        Sortable.utils.deselect(node);
      });

      this.selectedGroups.splice(0);
      this.selectedJobs.splice(0);
    },

    reload() {
      const date = this.date;
      Echo.leave(`plan.${moment(date).format('YYYY-MM-DD')}`);
      this.date = null;

      setTimeout(() => {
        this.date = date;
      }, 200);
    },

    startTimeGroup(group) {
      if (!group.startTime || group.startTime.length === 0) {
        return '-';
      }

      return `${group.startTime}`.substring(0, 5);
    },

    driverAvailability(driver) {
      let timeSlots = [];
      let locationSlots = [];

      if (!driver.availabilities || driver.availabilities.length === 0) {
        return '';
      }

      driver.availabilities.forEach(availability => {
        if (availability.available_all_day) {
          timeSlots.push('Hele dag beschikbaar');
        } else {
          timeSlots.push(moment(availability.available_from).format('HH:mm') + '-' + moment(availability.available_to).format('HH:mm'));
        }

        const fromLocation = `${availability.from_location_city} (${availability.from_location_province_short})`;
        const toLocation = `${availability.to_location_city} (${availability.to_location_province_short})`;

        locationSlots.push(`${fromLocation} - ${toLocation}`);
      });

      timeSlots = timeSlots.join(" & ");
      locationSlots = locationSlots.join("\n");

      return `${timeSlots}\n${locationSlots}`;
    },

    findGroupByJob(jobId, groups) {
      return groups.find(group => {
        return group.jobs.some(job => {
          return job.id === jobId
        }) > 0;
      });
    },

    findGroupById(groupId, groups) {
      return groups.find(group => {
        return group.group.uuid === groupId;
      });
    },

    groupRoute(group) {
      const currentIndex = group.group.current_index;

      let jobIndex = 0;
      return group.jobs.map((job, index) => {
        const rides = job.job_rides.map(jobRide => {
          let tag = 'span';

          if (job.canceled || group.group.finished || jobIndex < currentIndex) {
            if (job.canceled && index > 0 && !group.jobs[index - 1].finished) {
              tag = 'span';
            } else {
              tag = 'strong';
            }
          }
          const fromLocation = `<${tag}><span data-copy-click>${jobRide.from_location_city}</span> (${jobRide.from_location_province_short})</${tag}>`;

          jobIndex++;

          tag = 'span';
          if (job.canceled || group.group.finished || jobIndex < currentIndex) {
            if (job.canceled && index > 0 && !group.jobs[index - 1].finished) {
              tag = 'span';
            } else {
              tag = 'strong';
            }
          }

          const toLocation = `<${tag}><span data-copy-click>${jobRide.to_location_city}</span> (${jobRide.to_location_province_short})</${tag}>`;

          jobIndex++;

          return `<span>${fromLocation} - ${toLocation}`;
        }).join(' - ');

        return `<span class="job">${rides}</span>`;
      }).join('<span class="spacer px-10" style="padding: 0 20px;">|</span>')
    },

    confirmWhenPlanned(callback, cancelCallback, swal = true) {
      if (swal) {
        this.$swal({
          title: 'Ingeplande rit wijzigen',
          text: 'Deze groep is al ingepland. Wilt u de planning wijzigen?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ja, wijzig',
          cancelButtonText: 'Nee, annuleren',
          returnFocus: false,
        }).then((result) => {
          if (result.isConfirmed) {
            callback();
          } else {
            cancelCallback()
          }
        });
      } else {
        callback();
      }
    },

    confirmFinalize() {
      const text = "Weet je zeker dat je alle gekoppelde chauffeur(s) definitief wilt inplannen?";

      const selection = this.groupsWithDriver.map(group => group.group.uuid);

      this.$swal({
        title: 'Planning definitief maken',
        text,
        showCancelButton: true,
        confirmButtonText: 'Ja, inplannen',
        cancelButtonText: 'Nee, annuleren',
      }).then((result) => {
        if (result.isConfirmed) {
          const actionId = uuidv4();
          const data = [];
          let newGroups = _.cloneDeep(this.groups);

          this.groupsWithDriver.forEach(groupWithDriver => {
            newGroups = newGroups.map(group => {
              if (group.group.uuid === groupWithDriver.group.uuid) {
                group.group.finalized_at = moment().format();
                group.group.finalized_by = this.user.name;
                group.group.main_group_id = 0;

                data.push({
                  id: groupWithDriver.group.uuid,
                  driver: group.driver.id,
                  date: this.date,
                });
              }

              return group;
            });
          });

          this.groups = newGroups;
          this.echoChannel.whisper('group:finalize', { selection });
          this.saving = true;
          this.$http.post('/plannen/finalize?date=' + moment(this.date).format('YYYY-MM-DD') + '&actionId=' + actionId, data).then(response => {
            this.saving = false;

            notify({
              title: "Chauffeur(s) ingepland",
              text: "Alle gekoppelde chauffeurs zijn definitief ingepland.",
              type: 'success',
            });
          });
        }
      });
    },

    async updateGroup(groupId) {
      let newGroups = _.cloneDeep(this.groups);
      const data = {
        params: {
          date: moment(this.date).format('YYYY-MM-DD')
        },
      };

      await this.$http.get(`/plannen/ajax/${groupId}`, data).then(response => {
        newGroups = newGroups.map(group => {
          if (response.body[0] && group.group.uuid === groupId && response.body[0].group.uuid === groupId) {
            const clonedGroup = _.cloneDeep(response.body[0]);
            clonedGroup.driver = (clonedGroup.driver_id && clonedGroup.driver_id in this.allDrivers) ? this.allDrivers[clonedGroup.driver_id] : null;

            return clonedGroup;
          }

          return group;
        })
      });

      this.groups = newGroups;
    },

    updateGroupStatus(groupId, innerHTML = null) {
      let groupStatuses = document.getElementsByClassName('status-group');
      if (groupId) {
        groupStatuses = document.querySelectorAll(`[data-groupid="${groupId}"] .status-group`);
      }

      Array.from(groupStatuses).forEach(groupStatus => {
        if (innerHTML !== null) {
          groupStatus.innerHTML = innerHTML;
        } else {
          const groupElement = groupStatus.closest('.col-group');

          const statuses = Array.from(groupElement.querySelectorAll('td.status')).map(statusElement => {
            if (statusElement.innerText) {
              return statusElement.innerHTML;
            }

            return null;
          }).filter(status => {
            return status !== null
          });

          if (statuses.length > 0) {
            groupStatus.innerHTML = statuses[statuses.length - 1];
          } else {
            groupStatus.innerHTML = '';
          }
        }
      });
    },

    beforeWindowUnload(e) {
      if (this.saving) {
        e.returnValue = 'onbeforeunload';

        return false;
      }
    },
  },
}
</script>

<style>
.app-multiselect.multiselect .multiselect__tags {
  height: 52px !important;
  margin-bottom: -2px;
}

.app-multiselect.multiselect .multiselect__tags .multiselect__placeholder {
  color: gray !important;
  font-size: 14px !important;
}

.app-multiselect.multiselect .multiselect__select:before {
  display: none !important;
}

.card.card-fancy-table .table.table-bordered > tbody > tr > td {
  white-space: normal;
}

.driver-select-placeholder:hover {
  background-color: #efefef;
}

.sortable-ghost {
  cursor: move;
}

.plan-today tr.finished {
  opacity: .3;
}

img.red {
  /* #ea4242 */
  filter: invert(22%) sepia(36%) saturate(6006%) hue-rotate(357deg) brightness(98%) contrast(121%)
}

.group-route strong { color: #000; }
.tooltip {
  white-space: pre-line;
}

.finalized {
  transition: margin .4s;
}

.app-multiselect.multiselect .multiselect__option:not(.multiselect__option--group) {
  padding-left: 12px !important;
}
.app-multiselect.multiselect .multiselect__option:not(.multiselect__option--group):before {
  display: none !important;
}

.app-multiselect.multiselect .multiselect__tags .multiselect__single {
  color: #000 !important;
}

.app-multiselect.multiselect .multiselect__option:not(.multiselect__option--group).multiselect__option--selected {
  background-color: #efefef !important;
}
</style>



// WEBPACK FOOTER //
// resources/js/components/plan/Planscreen.vue